import type { RelatedPlace } from "~/lib/api";
import type { RelatedPlaceCategory } from "~/lib/relatedPlaces";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { RelatedPlaceItem } from "./RelatedPlaceItem";

interface PlaceCategoryProps {
  category: RelatedPlaceCategory;
  places: RelatedPlace[];
  canEdit?: boolean;
  onAdd?: () => void;
  onRemove?: (placeId: string) => void;
  onSelect?: (place: RelatedPlace) => void;
  removingId?: string | null;
}

export function PlaceCategory({
  category,
  places,
  canEdit,
  onAdd,
  onRemove,
  onSelect,
  removingId,
}: PlaceCategoryProps) {
  const isEmpty = places.length === 0;

  return (
    <div role="group" aria-label={category.label} className="space-y-2">
      <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <Icon i={category.icon} className="size-4" />
        <span className="flex-1">{category.label}</span>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-1 py-0 text-xs"
            onClick={onAdd}
            aria-label={`Add ${category.label.toLowerCase()}`}
          >
            + Add
          </Button>
        )}
      </div>
      {isEmpty ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label={`Add ${category.label.toLowerCase()}`}
          className="w-full rounded-md border border-dashed border-muted-foreground/25 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-muted-foreground/50"
        >
          {category.emptyLabel}
        </button>
      ) : (
        <div className="flex gap-2 overflow-x-auto pt-2 pr-2 pb-1 -mt-1 touch-pan-x">
          {places.map((place) => (
            <RelatedPlaceItem
              key={place.id}
              place={place}
              icon={category.icon}
              onRemove={canEdit ? () => onRemove?.(place.id) : undefined}
              onSelect={onSelect ? () => onSelect(place) : undefined}
              disabled={removingId === place.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
